import { lazy, Suspense, useEffect, useState } from "react";
import "./App.css";
import "./assests/css/style.css";
import {
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";
import { getQueryParams, gTagTracking, serviceDown, trackEvent } from "./utils/UtilityFunctions";
import { attachContentProtection } from "./utils/contentProtection";
import AlertComp from "./Alert";
import ProtectedRoute from "./ProtectedRoute";
import MainLoader from "./components/MainLoader";
import CookieConsent from "./components/CookieConsent";
import { postApi } from "./api/services";

// ── Route-level lazy loading ──────────────────────────────────────────────────
// Each chunk is downloaded only when its route is first visited.
// Heavy libraries (MediaPipe, PDF.js, AOS, survey engine) are excluded from
// the initial bundle entirely.
const LoginPage                 = lazy(() => import("./LoginPage"));
const OtpPage                   = lazy(() => import("./OtpPage"));
const ImgUpload                 = lazy(() => import("./ImgUpload"));
const Resultnew                 = lazy(() => import("./Resultnew"));
const OnBoardingSlider          = lazy(() => import("./OnBoardingSlider"));
const Reports                   = lazy(() => import("./Reports"));
const Aloxa                     = lazy(() => import("./Aloxa"));
const DesktopLandingSection     = lazy(() => import("./DesktopLandingSection"));
const WebHeader                 = lazy(() => import("./components/WebHeader"));
const Footer                    = lazy(() => import("./components/Footer"));
const PrescriptionList          = lazy(() => import("./PrescriptionList"));
const UserPrescription          = lazy(() => import("./UserPrescription"));
const Preview                   = lazy(() => import("./Preview"));
const TutorialVideo             = lazy(() => import("./components/TutorialVideo"));
const ALQIQuestionnaire         = lazy(() => import("./ALQIQuestionnaire"));
const SatisfactionQuestionnaire = lazy(() => import("./SatisfactionQuestionnaire"));
const HealthDeclaration         = lazy(() => import("./HealthDeclaration"));
const C1                        = lazy(() => import("./promos/C1"));
const QRCode                    = lazy(() => import("./QRCode"));
const StartTest                 = lazy(() => import("./promos/StartTest"));
const DoctorReport              = lazy(() => import("./DoctorReport"));
const SkipHairTest              = lazy(() => import("./SkipHairTest"));
const BookConsultation          = lazy(() => import("./BookConsultation"));
const ThankYouPage              = lazy(() => import("./ThankYouPage"));

// ── Synchronous mobile detection ─────────────────────────────────────────────
// Must run before the first render so DesktopLandingSection never mounts on
// mobile. Using useState lazy initializer guarantees it runs once synchronously
// (unlike useEffect which runs after the first paint).
const detectMobile = () =>
  window.cordova
    ? true
    : /iPhone|iPod|iPad|Android/i.test(navigator.userAgent);

// Module-level constants — computed once at load time, never change per session.
// Avoids recomputing className strings on every render.
const _IS_MOBILE       = detectMobile();
const DESKTOP_WEB_CLASS  = _IS_MOBILE ? "DesktopWebDiv mobile-view" : "DesktopWebDiv";
const MOBILE_HOLDER_CLASS = _IS_MOBILE ? "" : "mobileholderdiv";

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // Stable — reads the pre-computed module constant; no setter needed
  const [isMobile] = useState(() => _IS_MOBILE);

  const [isLoading, setIsLoading] = useState(false);
  const [alertopen, setalertopen] = useState(false);
  const [alertmsg, setalertmsg] = useState("progress");

  const locale = useSelector((state) => state.language.langData);

  // Derived once per render — replaces 5 repeated location.pathname comparisons
  const isC1    = location.pathname === '/c1';
  const isAloxa = location.pathname === '/aloxa';

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      console.log = () => {};
    }
  }, []);

  useEffect(() => attachContentProtection(), []);

  useEffect(() => {
    if (localStorage.getItem("testTaken")) {
      localStorage.clear();
      navigate('/');
    }
  }, []);

  // Merged: gender deep-link handler + hash query string redirect (both depend on location)
  useEffect(() => {
    const params = getQueryParams(window.location.href);
    if (params?.params?.gender) {
      localStorage.setItem('gender', params.params.gender);
      addUser(params.params.gender);
    }

    const hash = window.location.hash;
    const queryString = hash.split("?")[1];
    const checkUserData = JSON.parse(sessionStorage.getItem("checkUser"));
    if (queryString) {
      const queryParams = new URLSearchParams(queryString);
      if (queryParams.get("from")) {
        navigate(window.location.hash.split('?')[0].replace('#', '') || '/', { replace: true });
      } else if (!queryParams.get("from") && checkUserData) {
        navigate("/taketest");
      }
    }
  }, [location]);

  useEffect(() => {
    i18next.changeLanguage(locale || "en");
  }, [locale]);

  const addUser = async (urlGender) => {
    const userObj = JSON.parse(localStorage.getItem('user_obj'));
    if (!userObj) {
      setIsLoading(true);
      const genderVal = capitalizeFirstLetter(urlGender);
      const params = {
        year_of_birth: '',
        gender: genderVal,
        first_name: '',
        last_name: '',
        doctor_referral_code: null,
        nurse_referral_code: null,
        clinic_referral_code: null,
      };
      try {
        const res = await postApi('add_patient', params);
        await trackEvent('no_signup_initiated', 'success', " ", localStorage.getItem('phno'), res.user_obj.user_id);
        await gTagTracking('no_signup_initiated', {
          action: 'success',
          error: " ",
          mobile_number: localStorage.getItem('phno'),
          user_id: res.user_obj.user_id,
        });
        setIsLoading(false);
        if (res.error) {
          serviceDown(navigate, localStorage.getItem('channel') === 'alopexia-ads' ? '/c1' : '/start-test');
        } else {
          localStorage.setItem('user_obj', JSON.stringify(res.user_obj));
          navigate("/taketest", { state: "onboardingComplete" });
        }
      } catch (err) {
        await trackEvent('no_signup_initiated', 'fail', err, " ", "");
        await gTagTracking('no_signup_initiated', {
          action: 'fail',
          error: err,
          mobile_number: " ",
          user_id: "",
        });
        setIsLoading(false);
      }
    } else {
      navigate("/taketest", { state: "onboardingComplete" });
    }
  };

  const capitalizeFirstLetter = (str) => {
    if (typeof str !== 'string' || str.length === 0) return str;
    return str.charAt(0)?.toUpperCase() + str.slice(1);
  };

  return (
    <div className="App">
      {!isMobile && !isC1 && (
        <div className="desktop-header">
          <Suspense fallback={null}>
            <WebHeader />
          </Suspense>
        </div>
      )}

      <div className={DESKTOP_WEB_CLASS}>
        <div className={!isC1 ? "w-100 mainWrapper promopages" : "w-100 promopages"}>

          {!isMobile && !isC1 && (
            <Suspense fallback={<div className="desktopDivShow" />}>
              <DesktopLandingSection />
            </Suspense>
          )}

          <div className={!isC1 && isMobile ? "mobileSectionsDiv" : "mobileSectionsDiv desktopBg"}>
            <div className={MOBILE_HOLDER_CLASS}>
              {isLoading && <MainLoader />}

              <Suspense fallback={<MainLoader />}>
                <Routes location={location}>

                  <Route
                    path="/loginpage"
                    element={<ProtectedRoute element={<LoginPage />} />}
                  />
                  <Route
                    path="/otppage"
                    element={<ProtectedRoute element={<OtpPage />} />}
                  />
                  <Route
                    path="/taketest"
                    element={<ProtectedRoute element={<ImgUpload />} />}
                  />
                  <Route
                    path="/reports"
                    element={<ProtectedRoute element={<Reports />} />}
                  />
                  <Route
                    path="/report"
                    element={<ProtectedRoute element={<Resultnew />} />}
                  />
                  <Route
                    path="/aloxa"
                    element={<ProtectedRoute user={sessionStorage.getItem('checkUser')} element={<Aloxa />} />}
                  />
                  <Route
                    path="/start-test"
                    element={<StartTest />}
                  />
                  <Route
                    path="/c1"
                    element={<C1 />}
                  />
                  <Route
                    path="/tutorial"
                    element={<TutorialVideo />}
                  />
                  <Route
                    path="/preview"
                    element={<ProtectedRoute element={<Preview />} />}
                  />
                  {/* Temporarily disabled as per new consultation flow.
                      Do not delete. Will be required in future releases. */}
                  <Route
                    path="/healthdeclaration"
                    element={<ProtectedRoute element={<HealthDeclaration />} />}
                  />
                  <Route
                    path="/prescriptions"
                    element={<ProtectedRoute element={<PrescriptionList />} />}
                  />
                  <Route
                    path="/userprescription"
                    element={<ProtectedRoute element={<UserPrescription />} />}
                  />
                  <Route
                    path="/doctorreport"
                    element={<ProtectedRoute element={<DoctorReport />} />}
                  />
                  <Route
                    path="/alqi-questionnaire"
                    element={<ProtectedRoute element={<ALQIQuestionnaire />} />}
                  />
                  <Route
                    path="/satisfaction-questionnaire"
                    element={<ProtectedRoute element={<SatisfactionQuestionnaire />} />}
                  />
                  <Route
                    path="/selectgender"
                    element={<OnBoardingSlider />}
                  />
                  <Route
                    path="/skip-hair-test"
                    element={<ProtectedRoute element={<SkipHairTest />} />}
                  />
                  <Route
                    path="/book-consultation"
                    element={<ProtectedRoute element={<BookConsultation />} />}
                  />
                  <Route
                    path="/thank-you"
                    element={<ProtectedRoute element={<ThankYouPage />} />}
                  />
                  <Route
                    path="/qrcode"
                    element={<QRCode />}
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute element={<StartTest />} nopromo={true} routeUrl="/start-test" />
                    }
                  />

                </Routes>
              </Suspense>

            </div>
          </div>
        </div>

        {isC1 && <CookieConsent />}
      </div>

      {!isMobile && !isC1 && !isAloxa && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}

      <AlertComp open={alertopen} msg={alertmsg} type={"success"} />
    </div>
  );
}

export default App;
